import type React from 'react';
import { useCallback, useEffect } from 'react';
import { AxiosInterceptorsToken } from './token';
import { AxiosInterceptorsRefreshToken } from './refresh-token';
import { api, baseURL } from 'AurionCR/components';

import { AxiosInterceptorsSources } from './sources';
import { checkUserLogged } from 'AurionCR/store/modules/auth';
import { useDispatch } from 'react-redux';
import { AxiosInterceptorPost } from 'contexts/axios-interceptors/post';
import { AxiosInterceptorPatch } from 'contexts/axios-interceptors/patch';
import { getAuthTokens, updateAuthTokens } from 'utils/cookies';

const refresh = async (data: { token: string; refreshToken: string }) => {
  const response = await api.post('Auth/RefreshToken', data);
  const {
    data: { token },
  } = response;

  return fetch(`${baseURL}Account/GetCurrentAppUser`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  }).then((res) => {
    if (!res.ok) {
      throw new Error(res.statusText);
    }
    return response;
  });
};

export const AxiosInterceptors: React.FC = ({ children }) => {
  const onRefresh = useCallback(async () => {
    const { token, refreshToken, expires } = getAuthTokens();

    if (!token || !refreshToken || !expires) {
      throw new Error('token-not-exist');
    }

    const {
      data: { refreshToken: RefreshToken, token: Token },
    } = await refresh({ token, refreshToken });

    return { token: Token, refreshToken: RefreshToken, expires };
  }, []);

  const onError = useCallback(() => {
    if (window.location.pathname !== '/login') {
      window.location.href = `/login?redirect=${window.location.pathname}${window.location.search}`;
    }
  }, []);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(checkUserLogged());
  }, [dispatch]);

  return (
    <AxiosInterceptorsToken>
      <AxiosInterceptorsRefreshToken
        onRefresh={onRefresh}
        onSuccess={updateAuthTokens}
        onError={onError}
      >
        <AxiosInterceptorsSources>
          <AxiosInterceptorPost>
            <AxiosInterceptorPatch>{children}</AxiosInterceptorPatch>
          </AxiosInterceptorPost>
        </AxiosInterceptorsSources>
      </AxiosInterceptorsRefreshToken>
    </AxiosInterceptorsToken>
  );
};
